import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box,
  Typography,
  IconButton,
  useTheme,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import { Close, Save, Add, AutoAwesome } from '@mui/icons-material';
import { Task, TaskCreate, TaskUpdate } from '../../types/task';
import { suggestSubtasks } from '../../services/taskService';

interface TaskFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (task: TaskCreate | TaskUpdate) => void;
  task?: Task;
  title: string;
}

const TaskForm: React.FC<TaskFormProps> = ({ open, onClose, onSubmit, task, title }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<TaskCreate | TaskUpdate>({
    title: '',
    description: '',
    due_date: null,
    priority: 'medium',
    status: 'todo',
  });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestedSubtasks, setSuggestedSubtasks] = useState<string[]>([]);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description,
        due_date: task.due_date,
        priority: task.priority,
        status: task.status,
      });
    } else {
      setFormData({
        title: '',
        description: '',
        due_date: null,
        priority: 'medium',
        status: 'todo',
      });
    }
    setSuggestedSubtasks([]);
  }, [task, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Removed unused handleDateChange function

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Add subtasks to description if any were suggested and selected
    if (suggestedSubtasks.length > 0) {
      const subtasksText = suggestedSubtasks.map(task => `- [ ] ${task}`).join('\n');
      const updatedDescription = formData.description 
        ? `${formData.description}\n\n## Subtasks:\n${subtasksText}`
        : `## Subtasks:\n${subtasksText}`;
      
      onSubmit({ ...formData, description: updatedDescription });
    } else {
      onSubmit(formData);
    }
    
    setLoading(false);
    onClose();
  };

  const handleGenerateSubtasks = async () => {
    if (!formData.title || !formData.description) return;
    
    setAiLoading(true);
    try {
      const response = await suggestSubtasks({
        title: formData.title as string,
        description: formData.description as string
      });
      setSuggestedSubtasks(response.subtasks);
    } catch (error) {
      console.error('Error generating subtasks:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRemoveSubtask = (index: number) => {
    setSuggestedSubtasks(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'rgba(30, 30, 30, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        pb: 2
      }}>
        <Typography 
          variant="h5" 
          component="span"
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #00e5ff, #2979ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              required
              autoFocus
              variant="outlined"
              InputProps={{
                sx: {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                  }
                }
              }}
            />
            
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              InputProps={{
                sx: {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                  }
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="priority-label">Priority</InputLabel>
                <Select
                  labelId="priority-label"
                  name="priority"
                  value={formData.priority || 'medium'}
                  label="Priority"
                  onChange={handleSelectChange}
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                    }
                  }}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status || 'todo'}
                  label="Status"
                  onChange={handleSelectChange}
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                    }
                  }}
                >
                  <MenuItem value="todo">To Do</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="due-soon">Due Soon</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <TextField
              label="Due Date"
              name="due_date"
              type="date"
              value={formData.due_date ? new Date(formData.due_date).toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  // Set time to end of day (23:59:59) to give the full day for the task
                  const date = new Date(value);
                  date.setHours(23, 59, 59, 999);
                  setFormData(prev => ({ 
                    ...prev, 
                    due_date: date.toISOString()
                  }));
                } else {
                  setFormData(prev => ({ 
                    ...prev, 
                    due_date: null 
                  }));
                }
              }}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                sx: {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 2px rgba(0, 229, 255, 0.2)',
                  }
                }
              }}
            />
            
            {/* AI Subtask Generation */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  AI-Generated Subtasks
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={aiLoading ? <CircularProgress size={20} /> : <AutoAwesome />}
                  onClick={handleGenerateSubtasks}
                  disabled={aiLoading || !formData.title || !formData.description}
                  sx={{ 
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.main,
                    '&:hover': {
                      borderColor: theme.palette.secondary.light,
                      backgroundColor: 'rgba(255, 0, 229, 0.1)',
                    }
                  }}
                >
                  {aiLoading ? 'Generating...' : 'Generate Subtasks'}
                </Button>
              </Box>
              
              {suggestedSubtasks.length > 0 ? (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  borderRadius: '8px', 
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  {suggestedSubtasks.map((subtask, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: index < suggestedSubtasks.length - 1 ? 1 : 0,
                        p: 1,
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }
                      }}
                    >
                      <Typography variant="body2">
                        {subtask}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemoveSubtask(index)}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {aiLoading 
                    ? 'Generating subtasks...' 
                    : 'Fill in title and description, then click "Generate Subtasks" to get AI suggestions'}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            sx={{ 
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !formData.title}
            startIcon={loading ? <CircularProgress size={20} /> : task ? <Save /> : <Add />}
            sx={{ 
              background: 'linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)',
              boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
              '&:hover': {
                boxShadow: '0 0 20px rgba(0, 229, 255, 0.8)',
              }
            }}
          >
            {loading ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TaskForm;